/**
 * Auth gate chain: login redirect → email verify → name entry → GameSessionProvider mount.
 * Also hosts SwapWindowHelpModal (when allowTeamSwap && no swap yet), name-policy warning
 * modal, and TeamDetailRoot for authed gameplay routes.
 */
import { useContext, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Text, Heading, VStack, useDisclosure, Modal, Button, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay } from '@chakra-ui/react';
import type { User } from '@/models';
import type { TeamSelectionRecord } from '@/models/user';
import { AuthLoadingScreen } from '@/components/AuthCards/auth-ui';
import { SwapWindowHelpModal } from '@/components/modals/SwapWindowHelpModal';
import { TeamDetailRoot } from '@/components/teamDetail';
import { isAuthUserEmailVerified } from '@/helpers';
import { callAcknowledgeNamePolicyWarning } from '@/helpers/moderation/updateUserName';
import { ACTIVE_TOURNAMENT } from '@/constants';
import { type Dict } from '@/helpers';
import { FirebaseContext } from '@/session/FirebaseContext';
import { GameSessionProvider, useGameSession } from '@/session/GameSessionProvider';
import { useUserProfile } from '@/session/useUserProfile';
import { isProfileMissing, isProfileReady, isSessionLoading } from '@/session/types';
import { EnterNamePage } from '@/views/EnterNamePage';

export default function AuthLayout(props: { redirectTo: string }) {
  const ctx = useContext(FirebaseContext);
  const { userData, teamSelectionData, profilePhase } = useUserProfile();
  const loc = useLocation();
  const navigate = useNavigate();

  const needsVerifyRedirect =
    ctx.authReady && !ctx.authLoading && !!ctx.user && !isAuthUserEmailVerified(ctx.auth);
  const needsLoginRedirect = ctx.authReady && !ctx.authLoading && !ctx.user;

  // --- Gate logic: verify email, login redirect, profile loading ---

  useEffect(() => {
    if (needsVerifyRedirect) {
      navigate(`/verifyEmail?redirect_to=${encodeURIComponent(loc.pathname)}`, { replace: true });
    }
  }, [needsVerifyRedirect, loc.pathname, navigate]);

  useEffect(() => {
    if (needsLoginRedirect) {
      navigate(`${props.redirectTo}?redirect_to=${encodeURIComponent(loc.pathname)}`, { replace: true });
    }
  }, [needsLoginRedirect, loc.pathname, navigate, props.redirectTo]);

  if (needsVerifyRedirect) {
    return <AuthLoadingScreen message='Verify your email…' />;
  }

  if (needsLoginRedirect) {
    return <AuthLoadingScreen message='Loading…' />;
  }

  if (!ctx.authReady || ctx.authLoading || !ctx.user) {
    return <AuthLoadingScreen message='Loading…' />;
  }

  if (isProfileReady(profilePhase) && !!userData && (userData.name ?? '').length <= 0) {
    return <EnterNamePage />;
  }

  if (!isProfileReady(profilePhase) || !userData) {
    const loadingMessage = isProfileMissing(profilePhase)
      ? 'Creating your account…'
      : profilePhase === 'error'
        ? 'Could not load your profile. Please refresh.'
        : 'Loading your game…';
    return <AuthLoadingScreen message={loadingMessage} />;
  }

  return (
    <GameSessionProvider>
      <AuthLayoutOutlet teamSelectionData={teamSelectionData} userData={userData} />
    </GameSessionProvider>
  );
}

function AuthLayoutOutlet(props: {
  userData: User;
  teamSelectionData: Dict<TeamSelectionRecord>;
}) {
  const ctx = useContext(FirebaseContext);
  const { sessionPhase, allowTeamSwap } = useGameSession();
  const swapPromptDismissedThisLoadRef = useRef(false);
  const [nameWarningBusy, setNameWarningBusy] = useState(false);
  const teamSwapExplanationModalDisclosure = useDisclosure();
  const namePolicyWarningDisclosure = useDisclosure();

  // --- Team swap announcement modal (once per page load while window is open) ---

  function dismissTeamSwapPrompt() {
    swapPromptDismissedThisLoadRef.current = true;
    teamSwapExplanationModalDisclosure.onClose();
  }

  useEffect(() => {
    if (!allowTeamSwap) {
      swapPromptDismissedThisLoadRef.current = false;
      return;
    }

    if (isSessionLoading(sessionPhase) || sessionPhase === 'error') {
      return;
    }

    const tournamentDoc = props.teamSelectionData[ACTIVE_TOURNAMENT];
    const countryList = tournamentDoc?.countryList ?? [];
    const listIndexSwapPair = tournamentDoc?.listIndexSwapPair ?? null;

    if (
      countryList.length > 0 &&
      !listIndexSwapPair &&
      !swapPromptDismissedThisLoadRef.current
    ) {
      teamSwapExplanationModalDisclosure.onOpen();
    }
  }, [props.userData, allowTeamSwap, props.teamSelectionData, sessionPhase]);

  // --- Display-name policy warning (first strike) ---

  useEffect(() => {
    if (props.userData.namePolicyWarningPending) {
      namePolicyWarningDisclosure.onOpen();
    }
  }, [props.userData.namePolicyWarningPending]);

  async function acknowledgeNamePolicyWarning() {
    setNameWarningBusy(true);
    try {
      await callAcknowledgeNamePolicyWarning(ctx.funcs);
      namePolicyWarningDisclosure.onClose();
    } catch (err) {
      console.error('[AuthLayout] acknowledgeNamePolicyWarning', err);
    } finally {
      setNameWarningBusy(false);
    }
  }

  if (isSessionLoading(sessionPhase)) {
    return <AuthLoadingScreen message='Loading your game…' />;
  }

  if (sessionPhase === 'error') {
    return <AuthLoadingScreen message='Could not load game data. Please refresh.' />;
  }

  const countryList = props.teamSelectionData[ACTIVE_TOURNAMENT]?.countryList ?? [];

  // --- Outlet + team detail (canonical fixtures/results loaded inside TeamDetailRoot) ---

  const outlet = (
    <>
      <Outlet />
      <SwapWindowHelpModal
        isOpen={teamSwapExplanationModalDisclosure.isOpen}
        onClose={dismissTeamSwapPrompt}
      />
      <Modal
        isOpen={namePolicyWarningDisclosure.isOpen}
        onClose={() => undefined}
        closeOnOverlayClick={false}
        closeOnEsc={false}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Heading fontSize={24}>Display name reset</Heading>
          </ModalHeader>
          <ModalBody>
            <VStack spacing={4} align='stretch'>
              <Text>
                Your display name has been detected as offensive or inappropriate.
              </Text>
              <Text>
                If you use an offensive name again, <strong>your account may be permanently removed</strong>.
              </Text>
              <Text>You can choose a new name from your account settings.</Text>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme='green'
              isLoading={nameWarningBusy}
              onClick={() => void acknowledgeNamePolicyWarning()}
            >
              I understand
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );

  return (
    <TeamDetailRoot countryList={countryList}>
      {outlet}
    </TeamDetailRoot>
  );
}
